import React from 'react';
import { useTranslation } from 'react-i18next';
import SimpleModal from 'ui/Modals/SimpleModal';
import ArgumentsListGrid from './ArgumentsListGrid';
import useGetArgumentsByExerciceByBudgets from '../helpers/useGetArguments';
import Loading from 'components/Loading';
import { compareArgumentsWithResourceName } from '../helpers/dataHelpers';

type ArgumentsListModalProps = {
    isOpen: boolean;
    onClose: () => void;
    exercice: number;
    budgetNames?: string | null;
};

/**
 * A component that renders a modal displaying a list of arguments.
 *
 * @param isOpen - Indicates whether the modal is open or closed.
 * @param onClose - Function to be called when the modal is closed.
 * @param exercice - The exercise for which arguments are being displayed.
 * @param budgetNames - Optional string containing budget names to filter arguments. Format should be "NAME1|NAME2"
 * @returns The rendered modal displaying arguments within an ArgumentsListGrid component.
 * ```
 */
const ArgumentsListModal = ({
    isOpen,
    onClose,
    exercice,
    budgetNames,
}: ArgumentsListModalProps): JSX.Element => {
    const { t } = useTranslation();

    const { data: budgetsArguments, isLoading: argumentsLoading } =
        useGetArgumentsByExerciceByBudgets(exercice, isOpen, budgetNames);

    return (
        <SimpleModal
            open={isOpen}
            onClose={onClose}
            dialogTitle={t('analyseArguments.argumentsListModalTitle')}
            maxWidth="xl"
        >
            {argumentsLoading ? (
                <Loading />
            ) : (
                <ArgumentsListGrid
                    rowData={budgetsArguments?.sort(
                        compareArgumentsWithResourceName,
                    )}
                    isOpen={isOpen}
                />
            )}
        </SimpleModal>
    );
};

export default ArgumentsListModal;
